import { useState } from 'react';
import { Zap, TerminalSquare } from 'lucide-react';

import { useAppSelector } from 'app/store';
import { selectUserInfo } from 'app/store/user';
import { maskApiKey } from 'app/utils';

import { ReactComponent as TypescriptIcon } from 'assets/icons/languages/typescript.svg';
import { ReactComponent as PythonIcon } from 'assets/icons/languages/python.svg';
import { ReactComponent as JavaIcon } from 'assets/icons/languages/java.svg';
import { ReactComponent as RubyIcon } from 'assets/icons/languages/ruby.svg';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Typography } from 'components/ui/typography';

import {
  createTypescriptCodeExample,
  createPythonCodeExample,
  createJavaCodeExample,
  createRubyCodeExample,
  createCurlCodeExample,
} from '../utils/codeExamples';
import { CodeBlock } from './CodeBlock';

type Language = 'typescript' | 'python' | 'java' | 'ruby' | 'curl';

const languageConfig: Record<
  Language,
  { label: string; icon: React.ReactNode; highlighter: string }
> = {
  typescript: {
    label: 'TypeScript',
    icon: <TypescriptIcon className="size-4" />,
    highlighter: 'typescript',
  },
  python: { label: 'Python', icon: <PythonIcon className="size-4" />, highlighter: 'python' },
  java: { label: 'Java', icon: <JavaIcon className="size-4" />, highlighter: 'kotlin' },
  ruby: { label: 'Ruby', icon: <RubyIcon className="size-4" />, highlighter: 'ruby' },
  curl: { label: 'cURL', icon: <TerminalSquare className="size-4" />, highlighter: 'bash' },
};

export const QuickStartSection = () => {
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('typescript');
  const { apiKey = '' } = useAppSelector(selectUserInfo) || {};

  const codeExamples: Record<Language, string> = {
    typescript: createTypescriptCodeExample(apiKey),
    python: createPythonCodeExample(apiKey),
    java: createJavaCodeExample(apiKey),
    ruby: createRubyCodeExample(apiKey),
    curl: createCurlCodeExample(apiKey),
  };

  const currentConfig = languageConfig[selectedLanguage];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <Zap className="h-5 w-5" />
          <CardTitle>Quick Start</CardTitle>
        </div>
        <CardDescription>
          Complete order flow example using the Rye Checkout Intents{' '}
          <Typography variant="link" asChild>
            <a href="https://docs.rye.com/api-v2/sdk" target="_blank">
              SDKs
            </a>
          </Typography>
          , or API.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Select
          value={selectedLanguage}
          onValueChange={(value: Language) => setSelectedLanguage(value)}
        >
          <SelectTrigger className="w-48">
            <SelectValue>
              <span className="flex items-center gap-2">
                {currentConfig.icon}
                {currentConfig.label}
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(languageConfig) as Language[]).map((lang) => (
              <SelectItem key={lang} value={lang}>
                <span className="flex items-center gap-2">
                  {languageConfig[lang].icon}
                  {languageConfig[lang].label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <CodeBlock
          code={codeExamples[selectedLanguage]}
          mask={maskApiKey}
          language={currentConfig.highlighter}
        />
      </CardContent>
    </Card>
  );
};
